import { useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import {
  isPushNotificationsAvailable,
  checkPushPermissions,
  initializePushNotifications,
  registerForPushNotifications,
  saveDeviceToken,
  removeDeviceToken,
} from '@/services/pushNotifications';
import { toast } from 'sonner';

export interface UseNativePushOptions {
  userId?: string;
}

export interface UseNativePushReturn {
  isNativePlatform: boolean;
  nativePushAvailable: boolean;
  nativePushPermission: 'granted' | 'denied' | 'prompt';
  nativePushEnabled: boolean;
  isNativeSubscribing: boolean;
  currentDeviceToken: string | null;
  enableNativePush: () => Promise<void>;
  disableNativePush: () => Promise<void>;
}

export function useNativePush({ userId }: UseNativePushOptions): UseNativePushReturn {
  const [isNativePlatform] = useState(() => Capacitor.isNativePlatform());
  const [nativePushAvailable] = useState(() => isPushNotificationsAvailable());
  const [nativePushPermission, setNativePushPermission] = useState<'granted' | 'denied' | 'prompt'>('prompt');
  const [nativePushEnabled, setNativePushEnabled] = useState(false);
  const [isNativeSubscribing, setIsNativeSubscribing] = useState(false);
  const [currentDeviceToken, setCurrentDeviceToken] = useState<string | null>(null);

  useEffect(() => {
    async function checkNativePush() {
      if (!nativePushAvailable) return;

      try {
        const status = await checkPushPermissions();
        setNativePushPermission(status.receive as 'granted' | 'denied' | 'prompt');

        const storedToken = localStorage.getItem('currentDeviceToken');
        if (storedToken && status.receive === 'granted') {
          setNativePushEnabled(true);
          setCurrentDeviceToken(storedToken);
        }
      } catch {
        // suppressed
      }
    }

    checkNativePush();
  }, [nativePushAvailable]);

  const enableNativePush = useCallback(async () => {
    if (!nativePushAvailable || !userId) return;

    setIsNativeSubscribing(true);

    try {
      await initializePushNotifications({
        onRegistration: async (token) => {
          try {
            const deviceInfo = await Device.getInfo();

            await saveDeviceToken(userId, token.value, {
              model: deviceInfo.model,
              osVersion: deviceInfo.osVersion,
              appVersion: import.meta.env.VITE_APP_VERSION || '1.0.0',
            });

            setCurrentDeviceToken(token.value);
            setNativePushEnabled(true);
            toast.success('Native push notifications enabled!');
          } catch {
            toast.error('Failed to save device token');
          }
          setIsNativeSubscribing(false);
        },
        onRegistrationError: () => {
          toast.error('Failed to register for push notifications');
          setIsNativeSubscribing(false);
        },
      });

      await registerForPushNotifications();

      const status = await checkPushPermissions();
      setNativePushPermission(status.receive as 'granted' | 'denied' | 'prompt');

      if (status.receive === 'denied') {
        toast.error('Push notification permission denied. Please enable in device settings.');
        setIsNativeSubscribing(false);
      }
    } catch {
      toast.error('Failed to enable push notifications');
      setIsNativeSubscribing(false);
    }
  }, [nativePushAvailable, userId]);

  const disableNativePush = useCallback(async () => {
    if (!userId || !currentDeviceToken) return;

    setIsNativeSubscribing(true);

    try {
      await removeDeviceToken(userId, currentDeviceToken);
      setNativePushEnabled(false);
      setCurrentDeviceToken(null);
      toast.success('Native push notifications disabled');
    } catch {
      toast.error('Failed to disable push notifications');
    } finally {
      setIsNativeSubscribing(false);
    }
  }, [userId, currentDeviceToken]);

  return {
    isNativePlatform,
    nativePushAvailable,
    nativePushPermission,
    nativePushEnabled,
    isNativeSubscribing,
    currentDeviceToken,
    enableNativePush,
    disableNativePush,
  };
}
